// =====================================================
// EC Gift Manager v2.4 — Dynamic 3-Tier Goodies
// Config from theme settings via #ec-gift-global-config
// Uses fetch/XHR interception so gifts sync AFTER any
// cart mutation, regardless of which JS file triggers it.
// Single render: gifts are in cart before response returns.
// =====================================================
(function() {
  'use strict';

  var CFG = null;
  var ec_init_done = false;

  /* Queued promise chain — serializes all processGifts calls */
  var _giftQueue = Promise.resolve({ changed: false });

  /* Flag: when true, our own cart requests bypass the interceptor */
  window.__ec_gift_internal = false;

  /* ============================================================
     FETCH INTERCEPTOR
     Wraps window.fetch so any POST to /cart/add, /cart/change,
     /cart/update triggers ecSyncGifts AFTER the response.
     The original Response is returned to the caller unchanged,
     but gifts have been synced before the caller sees it.
     ============================================================ */
  var _originalFetch = window.fetch;
  var _cartMutationRe = /\/cart\/(add|change|update)(\.js|\.json)?(\?|$)/i;

  window.fetch = function(input, init) {
    var url = (typeof input === 'string') ? input : (input && input.url ? input.url : '');
    var method = ((init && init.method) || 'GET').toUpperCase();

    /* Only intercept POST to cart mutation endpoints */
    if (method !== 'POST' || !_cartMutationRe.test(url) || window.__ec_gift_internal) {
      return _originalFetch.apply(this, arguments);
    }

    console.log('[EC Gift] Intercepted fetch POST:', url);

    /* Call original fetch, wait for response, then sync gifts */
    return _originalFetch.apply(this, arguments).then(function(response) {
      /* Clone response so caller can still read it */
      var cloned = response.clone();

      /* Chain gift sync — wait for it before returning response */
      return window.ecSyncGifts(true).then(function() {
        return cloned;
      });
    });
  };

  /* ============================================================
     XMLHttpRequest INTERCEPTOR
     Wraps XHR.send for jQuery.ajax calls (Shopify.addItem,
     Shopify.changeItem, Shopify.removeItem in global.js).
     After the response arrives, we run ecSyncGifts before
     calling the original onreadystatechange/onload callbacks.
     ============================================================ */
  var _xhrOrigOpen = XMLHttpRequest.prototype.open;
  var _xhrOrigSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url) {
    this.__ecMethod = method;
    this.__ecUrl = url;
    return _xhrOrigOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function(body) {
    var xhr = this;
    var method = (xhr.__ecMethod || 'GET').toUpperCase();
    var url = xhr.__ecUrl || '';

    if (method !== 'POST' || !_cartMutationRe.test(url) || window.__ec_gift_internal) {
      return _xhrOrigSend.apply(this, arguments);
    }

    console.log('[EC Gift] Intercepted XHR POST:', url);

    /* Replace onreadystatechange to inject gift sync */
    var origOnRSC = xhr.onreadystatechange;
    var origOnLoad = xhr.onload;
    var giftSynced = false;

    function maybeSync(cb, args) {
      if (xhr.readyState === 4 && !giftSynced) {
        giftSynced = true;
        window.ecSyncGifts(true).then(function() {
          if (typeof cb === 'function') cb.apply(xhr, args || []);
        });
      } else {
        if (typeof cb === 'function') cb.apply(xhr, args || []);
      }
    }

    xhr.onreadystatechange = function() {
      maybeSync(origOnRSC, arguments);
    };

    /* Also handle onload which jQuery sometimes uses */
    if (origOnLoad) {
      xhr.onload = function() {
        if (!giftSynced) {
          giftSynced = true;
          window.ecSyncGifts(true).then(function() {
            origOnLoad.apply(xhr, arguments);
          });
        } else {
          origOnLoad.apply(xhr, arguments);
        }
      };
    }

    return _xhrOrigSend.apply(this, arguments);
  };

  /* ============================================================
     CONFIG & TIER LOGIC
     ============================================================ */
  function loadConfig() {
    var el = document.getElementById('ec-gift-global-config');
    if (!el) { console.warn('[EC Gift] No config element'); return false; }
    try {
      CFG = JSON.parse(el.textContent);
    } catch(e) {
      console.warn('[EC Gift] Config parse error — gift logic disabled.', e);
      return false;
    }
    if (!CFG || !Array.isArray(CFG.tiers)) { console.warn('[EC Gift] Invalid config'); return false; }
    CFG.tiers = CFG.tiers.filter(function(t) { return t && t.variantId != null && t.available !== false; });
    return true;
  }

  function countForTier(items, tier) {
    if (!CFG) return 0;
    var filters = (tier.variantFilter || '').toLowerCase().split(',')
      .map(function(s) { return s.trim(); }).filter(Boolean);
    var count = 0;

    items.forEach(function(item) {
      if (CFG.skoeProductIds.indexOf(item.product_id) === -1) return;
      var vtitle = (item.variant_title || '').toLowerCase();
      var hasSize = /\d+\s*ml/i.test(vtitle);

      if (filters.length > 0) {
        var matched = filters.some(function(f) { return vtitle.indexOf(f) !== -1; });
        if (matched) { count += item.quantity; }
        else if (!hasSize && tier.countNoSize) { count += item.quantity; }
      } else {
        count += item.quantity;
      }
    });
    return count;
  }

  function countAllSkoe(items) {
    if (!CFG) return 0;
    var n = 0;
    items.forEach(function(item) {
      if (CFG.skoeProductIds.indexOf(item.product_id) !== -1) n += item.quantity;
    });
    return n;
  }

  /* ============================================================
     CORE GIFT PROCESSING
     ============================================================ */
  async function processGifts(skipRefresh) {
    if (!CFG) return { changed: false };

    try {
      window.__ec_gift_internal = true;
      var resp = await _originalFetch('/cart.js');
      var cart = await resp.json();
      window.__ec_gift_internal = false;

      var items = cart.items;
      var updates = {};
      var additions = [];
      var changed = false;
      var unlockedTiers = [];

      /* Pre-compute cart value excl. gifts (for cart-threshold on last tier) */
      var cartEur = 0;
      if (CFG.cartThreshold > 0) {
        var cartExcl = 0;
        items.forEach(function(it) {
          var isGift = (CFG.giftProductIds || []).indexOf(it.product_id) !== -1;
          if (!isGift) cartExcl += it.original_line_price;
        });
        cartEur = cartExcl / 100;
      }

      var lastTierIdx = CFG.tiers.length - 1;

      CFG.tiers.forEach(function(tier, idx) {
        if (!tier.variantId) return;

        var tierCount = countForTier(items, tier);
        var needed = tierCount >= tier.qty;

        /* For the last tier ALSO check cart-threshold (OR condition) */
        if (idx === lastTierIdx && CFG.cartThreshold > 0 && cartEur >= CFG.cartThreshold) {
          needed = true;
          console.log('[EC Gift] Tier', idx + 1, 'unlocked via cart threshold (€' + cartEur.toFixed(2) + ' >= €' + CFG.cartThreshold + ')');
        }

        if (tier.available === false) needed = false;

        var inCart = items.find(function(i) { return i.id === tier.variantId; });

        if (needed) {
          unlockedTiers.push(idx);
          if (!inCart) {
            additions.push({ id: tier.variantId, quantity: 1 });
            console.log('[EC Gift] Adding tier', idx + 1, tier.label);
          } else if (inCart.quantity > 1) {
            updates[tier.variantId] = 1;
          }
        } else {
          if (inCart) {
            updates[tier.variantId] = 0;
            console.log('[EC Gift] Removing tier', idx + 1, tier.label);
          }
        }
      });

      /* Execute removals */
      if (Object.keys(updates).length > 0) {
        window.__ec_gift_internal = true;
        await _originalFetch((window.Shopify.routes.root || '/') + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        });
        window.__ec_gift_internal = false;
        changed = true;
      }

      /* Execute additions */
      if (additions.length > 0) {
        window.__ec_gift_internal = true;
        await _originalFetch((window.Shopify.routes.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: additions })
        });
        window.__ec_gift_internal = false;
        changed = true;
      }

      if (changed) {
        console.log('[EC Gift] Cart updated with gifts.');
        window.__ec_gift_internal = true;
        var newResp = await _originalFetch('/cart.js');
        var newCart = await newResp.json();
        window.__ec_gift_internal = false;

        document.querySelectorAll('[data-cart-count]').forEach(function(el) {
          el.textContent = newCart.item_count;
        });

        if (!skipRefresh) {
          if (typeof window.ecRefreshSidebarCart === 'function') {
            window.ecRefreshSidebarCart();
          } else {
            var qc = document.querySelector('quick-cart-items[data-view="ajax_side_cart"]');
            if (qc && typeof qc.update === 'function') qc.update(newCart);
          }
        }

        window.dispatchEvent(new CustomEvent('ec:gifts_updated', {
          detail: { cart: newCart, unlockedTiers: unlockedTiers, skoeCount: countAllSkoe(newCart.items) }
        }));

        return { changed: true, cart: newCart };
      } else {
        return { changed: false, cart: cart };
      }
    } catch(err) {
      console.error('[EC Gift] Error:', err);
      window.__ec_gift_internal = false;
      return { changed: false };
    }
  }

  /* ============================================================
     GLOBAL API
     ============================================================ */
  window.ecSyncGifts = function(skipRefresh) {
    if (!CFG) {
      if (!loadConfig()) return Promise.resolve({ changed: false });
    }
    _giftQueue = _giftQueue
      .catch(function() {})
      .then(function() { return processGifts(skipRefresh); });
    return _giftQueue;
  };

  /* Safety-net: debounced listener for edge cases */
  var _giftDebounceTimer = null;
  function debouncedSyncGifts() {
    clearTimeout(_giftDebounceTimer);
    _giftDebounceTimer = setTimeout(function() {
      if (!window.__ec_gift_internal) {
        window.ecSyncGifts(false);
      }
    }, 2000);
  }
  window.addEventListener('cart_changed', debouncedSyncGifts);
  window.addEventListener('ec_cart_updated', debouncedSyncGifts);

  /* Initial load */
  function init() {
    if (ec_init_done) return;
    ec_init_done = true;
    if (loadConfig()) {
      setTimeout(function() { window.ecSyncGifts(false); }, 1500);
    }
  }
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(init, 200);
  } else {
    document.addEventListener('DOMContentLoaded', function() { setTimeout(init, 200); });
  }

  /* ============================================================
     HIDE QUANTITY CONTROLS FOR GIFT PRODUCTS
     ============================================================ */
  function hideGiftControls() {
    if (!CFG) return;
    var giftIds = CFG.giftVariantIds || [];
    if (!giftIds.length) return;

    giftIds.forEach(function(vid) {
      if (!vid) return;
      var sels = [
        '[data-variant-id="' + vid + '"]',
        '[data-cart-item-key*="' + vid + '"]',
        'a[href*="variant=' + vid + '"]'
      ];
      sels.forEach(function(sel) {
        document.querySelectorAll(sel).forEach(function(el) {
          var ci = el.closest('.previewCart__item, .cart__row, .cart-item, .item--loadout, [data-cart-item], .mini-cart-item');
          if (!ci) return;
          ci.querySelectorAll('.quantity, .qty, .cart-qty, .quantity__selector, .quantity-wrapper, .previewCart__qty, .ec-cart-qty-picker').forEach(function(q) {
            q.style.display = 'none';
          });
          ci.querySelectorAll('.previewCart__remove, .cart__remove, .remove, .cart-item__remove').forEach(function(r) {
            r.style.display = 'none';
          });
        });
      });
    });
  }

  function observeCart() {
    hideGiftControls();
    document.querySelectorAll('#halo-cart-sidebar, .cart, #cart').forEach(function(c) {
      var mo = new MutationObserver(function() { setTimeout(hideGiftControls, 250); });
      mo.observe(c, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(observeCart, 2000);
  } else {
    document.addEventListener('DOMContentLoaded', function() { setTimeout(observeCart, 2000); });
  }

  console.log('[EC Gift] v2.4 loaded — fetch/XHR interceptor, unified tier+threshold decision');
})();
